import React, { useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";

import PageLayout from "../../_layout/PageLayout";
import {
  ActionsSection,
  ButtonsSection,
  DataSection,
  SubSection,
} from "../../../components/data-components/sections/DataSections";
import EditButton from "../../../components/buttons/edit-button/EditButton";
import FormField from "../../../components/form-field/FormField";
import { SettingsMenuItemsModel } from "../SettingsMenuItems";
import { ViewOrganizationAnalysisConfidence } from "../../../services/authorization/Permissions";
import { AnalysisConfidenceStorageModel } from "../../../services/organization-analysis-confidence/AnalysisConfidenceStorage";
import { api } from "../../../services/api/ApiService";
import { translationService } from "../../../services/translation/TranslationService";

export default observer(() => {
  const history = useHistory();
  const [settingsMenuItems] = useState(() => SettingsMenuItemsModel.create({}));
  const [analysisConfidenceStorage] = useState(() =>
    AnalysisConfidenceStorageModel.create({}, { api })
  );

  const analysisSectionSize = useMemo(
    () => Math.ceil(analysisConfidenceStorage.AnalysisLevels.length / 3),
    [analysisConfidenceStorage.AnalysisLevels.length]
  );

  const highlightSectionSize = useMemo(
    () => Math.ceil(analysisConfidenceStorage.HighlightLevels.length / 3),
    [analysisConfidenceStorage.HighlightLevels.length]
  );

  const translateName = useCallback(
    (name: string): string =>
      translationService.translate(
        `damages.highlight-detection.${name.toLowerCase()}`
      ),
    []
  );

  const mapToFormField = useCallback(
    (name: string, value: number) => (
      <FormField
        id={`field-${name}`}
        key={name}
        labelKey={`${name} (%)`}
        readOnly
        fieldValue={value}
      />
    ),
    []
  );

  return (
    <PageLayout
      id="analysis-confidence"
      titleKey="general.analysis-confidence"
      accessWithOneOfThePermissions={[ViewOrganizationAnalysisConfidence]}
      isLoading={analysisConfidenceStorage.isLoading}
      menuItems={{
        staticItems: settingsMenuItems.getStaticItems(),
      }}
    >
      <ActionsSection>
        <ButtonsSection>
          <EditButton onClick={() => history.push("/settings/analysis-confidence/edit")} />
        </ButtonsSection>
      </ActionsSection>
      <DataSection labelKey="ViewAnalysisConfidence.analysis.section-title">
        <SubSection>
          {analysisConfidenceStorage.AnalysisLevels.slice(undefined, analysisSectionSize).map((x) =>
            mapToFormField(x.name, x.level)
          )}
        </SubSection>
        <SubSection>
          {analysisConfidenceStorage.AnalysisLevels.slice(
            analysisSectionSize,
            analysisSectionSize * 2
          ).map((x) => mapToFormField(x.name, x.level))}
        </SubSection>
        <SubSection>
          {analysisConfidenceStorage.AnalysisLevels.slice(analysisSectionSize * 2).map((x) =>
            mapToFormField(x.name, x.level)
          )}
        </SubSection>
      </DataSection>
      <DataSection labelKey="ViewAnalysisConfidence.highlight.section-title">
        <SubSection>
          {analysisConfidenceStorage.HighlightLevels.slice(undefined, highlightSectionSize).map(
            (x) => mapToFormField(translateName(x.name), x.level)
          )}
        </SubSection>
        <SubSection>
          {analysisConfidenceStorage.HighlightLevels.slice(
            highlightSectionSize,
            highlightSectionSize * 2
          ).map((x) => mapToFormField(translateName(x.name), x.level))}
        </SubSection>
        <SubSection>
          {analysisConfidenceStorage.HighlightLevels.slice(highlightSectionSize * 2).map((x) =>
            mapToFormField(translateName(x.name), x.level)
          )}
        </SubSection>
      </DataSection>
    </PageLayout>
  );
});
